import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { DetailField } from '../../components/training/course/DetailField';
import { ProformaSection } from '../../components/training/billing/ProformaSection';
import { InvoiceSection } from '../../components/training/billing/InvoiceSection';
import { usePractices } from '../../hooks/usePractices';
import { useTrainingBilling } from '../../hooks/useTrainingBilling';
import { useTrainingCourses } from '../../hooks/useTrainingCourses';
import { useTeachers } from '../../hooks/useTeachers';
import { trainingBillingStore } from '../../services/trainingBillingStore';
import { statusesFromPractice, trainingBillingStatuses } from '../../data/trainingBillingOptions';
import { formatEuro } from '../../utils/currency';
import { practiceTotal, requesterKindLabel, requesterName } from '../../utils/practices';
import { billingStatusVariant, courseAssociation } from '../../utils/trainingBilling';
import { TrainingBillingStatus } from '../../types/trainingBilling';
export function TrainingBillingDetailPage() {
  const {
    practiceId
  } = useParams();
  const {
    practices
  } = usePractices();
  const records = useTrainingBilling();
  const courses = useTrainingCourses();
  const teachers = useTeachers();
  const practice = practices.find(p => p.id === practiceId);
  const record = records.find(r => r.practiceId === practiceId);
  if (!practice || !record) return <Navigate to="/training/fatturazione" replace data-id="element-6278" data-mp="36r1tagy02lpi" />;
  const assoc = courseAssociation(practice, courses, teachers);
  const amount = practiceTotal(practice);
  return <Layout data-id="element-6279" data-mp="1v7z6anzk1pkf">
      <Link to="/training/fatturazione" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-seform-navy mb-4" data-id="element-6280" data-mp="1vbmzfm1389ym">
        <ArrowLeft className="h-4 w-4" data-id="element-6281" data-mp="5r3m4tqzhbt8" /> Fatturazione
      </Link>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6" data-id="element-6282" data-mp="stp0dlp814z">
        <div data-id="element-6283" data-mp="3s7rddxvu50c4">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-6284" data-mp="3s77uadug85wi">{requesterName(practice)}</h1>
          <p className="text-slate-500 mt-1" data-id="element-6285" data-mp="3o6xqn7u4prkt">
            {requesterKindLabel(practice)} ·{' '}
            <Link to={`/training/pratiche/${practice.id}/modifica`} className="text-seform-orange hover:underline" data-id="element-6286" data-mp="3j7uiheiur58m">
              Apri pratica
            </Link>
          </p>
        </div>
        <Badge variant={billingStatusVariant(record.status)} data-id="element-6287" data-mp="399wums4wnagj">{record.status}</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-id="element-6288" data-mp="358v5x58m6gst">
        <div className="lg:col-span-2 space-y-6" data-id="element-6289" data-mp="17iabtdv62lv5">
          {record.proforma && <ProformaSection proforma={record.proforma} productLabel={assoc.productLabel} productCode={assoc.productCode} amount={amount} onSave={p => trainingBillingStore.saveProforma(practice.id, p)} data-id="element-6290" data-mp="13ggf1go3e209" />}
          {record.invoice && <InvoiceSection invoice={record.invoice} companyName={practice.clientType === 'anagrafica' ? practice.companyName : undefined} issuedProformaNumbers={trainingBillingStore.issuedProformaNumbers()} onSave={i => trainingBillingStore.saveInvoice(practice.id, i)} data-id="element-6291" data-mp="2v4o89d9xu2k5" />}
        </div>

        <aside className="space-y-6 lg:sticky lg:top-6" data-id="element-6292" data-mp="2bekqhbm6hga8">
          <Card title="Stato fatturazione" noHoverShadow data-id="element-6293" data-mp="2gepcfh5uv37u">
            <Select label="Stato" value={record.status} onChange={e => trainingBillingStore.setStatus(practice.id, e.target.value as TrainingBillingStatus)} options={trainingBillingStatuses.map(s => ({
            value: s,
            label: statusesFromPractice.includes(s) ? `${s} (da Pratiche)` : s
          }))} description='"Proforma da fare" e "Fattura da fare" arrivano dalla pratica' data-id="element-6294" data-mp="2gga48z0sc820" />
          </Card>

          <Card title="Associazione del corso" noHoverShadow data-id="element-6295" data-mp="3va8gocqeju3w">
            <dl className="space-y-4" data-id="element-6296" data-mp="d5neg7hlsukd">
              <DetailField label="Tipologia di corso + codice prodotto" data-id="element-6297" data-mp="d4lvzhy9d5sm">
                {assoc.productLabel} ·{' '}
                <span className="font-mono" data-id="element-6298" data-mp="84zj59mcdrkc">{assoc.productCode}</span>
              </DetailField>
              <DetailField label="Codice specifico del corso" data-id="element-6299" data-mp="my2qypa4sqxv">
                {assoc.courseCode && (assoc.course ? <Link to={`/training/corsi/${assoc.course.id}`} className="font-mono text-seform-orange hover:underline" data-id="element-6300" data-mp="vw4y7mkgkftl">
                      {assoc.courseCode}
                    </Link> : <span className="font-mono" data-id="element-6301" data-mp="1fi7z89a5j9z6">{assoc.courseCode}</span>)}
              </DetailField>
              {!assoc.isOnDemand && <DetailField label="Data di svolgimento" data-id="element-6302" data-mp="11ifb8lq0f7uw">{assoc.dateLabel}</DetailField>}
              <DetailField label="Docenti" data-id="element-6303" data-mp="15ih4q7llcuz3">
                {assoc.teachers.length > 0 ? assoc.teachers.join(', ') : undefined}
              </DetailField>
              <DetailField label="Importo pratica senza IVA" data-id="element-6304" data-mp="wjvncfruvlzw">
                {amount !== undefined && formatEuro(amount)}
              </DetailField>
            </dl>
          </Card>
        </aside>
      </div>
    </Layout>;
}